import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import type { UrlTree } from '@angular/router';
import type { IconDefinition } from '@fortawesome/angular-fontawesome';
import type { RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
    calculateAggregate,
    DashboardWidget,
    formatDashboardValue,
    isAggregationConfigured,
    ratioValueFormat,
} from '@h20-shared/dashboards';
import {
    KPI_CARD_NO_VALUE,
    PulseKpiCardComponent,
    PulseKpiCardGridComponent,
} from '@h20-shared/pulse-components/pulse-kpi-card';
import type {
    KpiCardGridDirection,
    KpiCardLink,
    KpiCardStatus,
    KpiCardValueSuffix,
} from '@h20-shared/pulse-components/pulse-kpi-card';
import { FALLBACK_LOCALE } from '@h20-shared/utils/number-format-utils';
import { translated } from '@h20-shared/utils/translate-utils';
import { DASHBOARD_WIDGET_MODE } from '../dashboard-widget-mode';
import { KPI_ICONS } from './dashboard-kpi.config';
import { KPI_DEFAULT_PRESENTATION } from './dashboard-kpi.schema';
import type { KpiCardConfig, KpiPresentation, KpiWidgetConfig } from './dashboard-kpi.schema';

/** A path inside this app, which is the only thing a card links to */
const APP_PATH = /^\/(?!\/)/;

/** One card as the template draws it: every string already formatted and translated. */
type KpiCardView = {
    readonly id: string;
    readonly label: string;
    readonly hint: string | undefined;
    readonly caption: string | undefined;
    readonly value: string | undefined;
    /** The `of 10` half of a `3 of 10` */
    readonly valueSuffix: KpiCardValueSuffix | undefined;
    readonly icon: IconDefinition | undefined;
    readonly iconColor: KpiCardStatus;
    readonly link: KpiCardLink | undefined;
    /** Where a clickable card leads. */
    readonly linkUrl: UrlTree | undefined;
    readonly interactive: boolean;
    /** Whether the author has named a value column yet. */
    readonly configured: boolean;
};

/** The formatted numbers an author may write into a caption. */
type KpiTokens = Readonly<Record<'value' | 'numerator' | 'denominator', string>>;

/**
 * A row of KPI cards computed from the cell's rows: one card per configured card, each reducing the
 * rows to a number, with an optional second number as a denominator.
 */
@Component({
    selector: 'pulse-dashboard-kpi',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, PulseKpiCardComponent, PulseKpiCardGridComponent],
    templateUrl: './dashboard-kpi.widget.html',
    styleUrl: './dashboard-kpi.widget.scss',
    host: { class: 'pulse-dashboard-widget--flush' },
})
export class DashboardKpiWidget extends DashboardWidget<RowObject, KpiWidgetConfig> {
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly authoring = inject(DASHBOARD_WIDGET_MODE) === 'authoring';
    private readonly langChange = toSignal(this.translate.onLangChange);

    protected readonly direction = computed<KpiCardGridDirection>(() =>
        this.config()?.direction === 'vertical' ? 'column' : 'row'
    );

    protected readonly presentation = computed<KpiPresentation>(
        () => this.config()?.presentation ?? KPI_DEFAULT_PRESENTATION
    );

    protected readonly cards = computed<KpiCardView[]>(() => {
        // Numbers are written in the current language, so a language change rebuilds them.
        this.langChange();
        const locale = this.translate.currentLang || FALLBACK_LOCALE;
        const rows = this.rows();
        return Object.entries(this.config()?.cards.items ?? {}).map(([id, card], index) =>
            this.buildCardView(id, index + 1, card, rows, locale)
        );
    });

    /** A viewer gets one explanation instead of a row of blank cards; an author sees the cards to fill in. */
    protected readonly unconfigured = computed(
        () => !this.authoring && this.cards().every((card) => !card.configured)
    );

    protected navigate(card: KpiCardView): void {
        if (card.linkUrl !== undefined) {
            void this.router.navigateByUrl(card.linkUrl);
        }
    }

    private buildCardView(
        id: string,
        cardNumber: number,
        card: KpiCardConfig,
        rows: readonly RowObject[],
        locale: string
    ): KpiCardView {
        const numerator = calculateAggregate(rows, card.numerator);
        const denominator = card.denominator.enabled
            ? calculateAggregate(rows, card.denominator)
            : undefined;
        // Neither half of a `3 of 10` is a fraction, so `percent` would multiply both by 100.
        const columnValueFormat =
            card.denominator.enabled && card.format === 'percent' ? 'decimal' : card.format;
        const shownNumerator = formatDashboardValue(numerator, columnValueFormat, locale);
        const shownDenominator = formatDashboardValue(denominator, columnValueFormat, locale);

        let value: string | undefined;
        let valueSuffix: KpiCardValueSuffix | undefined;
        if (!card.denominator.enabled) {
            value = shownNumerator;
        } else if (card.denominator.display === 'ratio') {
            if (
                typeof numerator === 'number' &&
                typeof denominator === 'number' &&
                denominator !== 0
            ) {
                value = formatDashboardValue(
                    numerator / denominator,
                    ratioValueFormat(card.format),
                    locale
                );
            }
        } else if (shownNumerator !== undefined && shownDenominator !== undefined) {
            // The denominator travels on its own so the card can draw it quieter than the number
            // the card is about.
            value = shownNumerator;
            valueSuffix = {
                word: translated(this.translate, 'Dashboard.KpiOfWord'),
                value: shownDenominator,
            };
        }

        // A caption reads as a sentence, so `{{value}}` stands for the whole phrase the card shows,
        // both halves of `3 of 10` included.
        const valuePhrase =
            valueSuffix === undefined
                ? value
                : translated(this.translate, 'Dashboard.KpiOf', {
                      numerator: shownNumerator,
                      denominator: shownDenominator,
                  });

        const tokens: KpiTokens = {
            value: valuePhrase ?? KPI_CARD_NO_VALUE,
            numerator: shownNumerator ?? KPI_CARD_NO_VALUE,
            denominator: shownDenominator ?? KPI_CARD_NO_VALUE,
        };
        const configured = isAggregationConfigured(card.numerator);

        const authoredHref = card.link.href.trim();
        const hasUsableLink = card.link.enabled && APP_PATH.test(authoredHref);
        const linkUrl =
            hasUsableLink && !this.authoring ? this.router.parseUrl(authoredHref) : undefined;

        // An unlabelled card would be a blank box in the editor.
        let label = card.label;
        if (!label && this.authoring) {
            label = translated(this.translate, 'Dashboard.KpiCardPlaceholder', {
                index: cardNumber,
            });
        }

        // The parser fills the caption's `{{name}}` tokens. A token with no value stays as typed.
        let caption =
            (this.translate.parser.interpolate(card.caption, tokens) ?? card.caption) || undefined;
        if (!configured && this.authoring) {
            caption = translated(this.translate, 'Dashboard.KpiCardUnconfigured');
        }

        // In the editor it is plain text, so an author cannot navigate away while editing.
        let link: KpiCardLink | undefined;
        if (card.link.label.trim() !== '' && hasUsableLink) {
            link =
                linkUrl === undefined
                    ? { label: card.link.label }
                    : { label: card.link.label, routerLink: linkUrl };
        }

        return {
            id,
            label,
            hint: card.tooltip || undefined,
            caption,
            value,
            valueSuffix,
            icon: card.icon.enabled ? KPI_ICONS[card.icon.name] : undefined,
            iconColor: card.icon.color,
            link,
            linkUrl,
            interactive: card.link.wholeCard && linkUrl !== undefined,
            configured,
        };
    }
}
